import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'react-hot-toast';

/**
 * ProfileCompletionBadge - Visual indicator showing profile completion status
 * Displays as a circular badge with percentage and color-coded progress
 * 
 * @param {Object} props
 * @param {number} props.completionPercentage - Completion percentage (0-100)
 * @param {'desktop'|'mobile'} props.variant - Display variant
 * @param {Function} props.onClick - Click handler for navigation
 * @param {string} props.className - Additional CSS classes
 */
const ProfileCompletionBadge = ({ 
  completionPercentage, 
  variant = 'desktop',
  onClick,
  className = ''
}) => {
  const { isDarkMode } = useTheme();
  const [previousPercentage, setPreviousPercentage] = useState(completionPercentage);
  const [celebrating, setCelebrating] = useState(false);

  // Detect when profile reaches 100% completion
  useEffect(() => {
    if (previousPercentage < 100 && completionPercentage >= 100) {
      // Trigger celebration
      setCelebrating(true);
      
      // Show success toast
      toast.success('🎉 Profile Complete! Great job!', {
        duration: 4000,
        icon: '✨',
      });
      
      // Remove badge after celebration
      setTimeout(() => {
        setCelebrating(false);
      }, 600);
    }
    
    setPreviousPercentage(completionPercentage);
  }, [completionPercentage, previousPercentage]);

  // Don't render if profile is complete (after celebration)
  if (completionPercentage >= 100 && !celebrating) {
    return null;
  }

  // Get color based on completion percentage
  const getColorClasses = () => {
    if (completionPercentage >= 75) {
      return isDarkMode
        ? 'bg-gradient-to-br from-green-600 to-emerald-600'
        : 'bg-gradient-to-br from-green-500 to-emerald-500';
    }
    if (completionPercentage >= 50) {
      return isDarkMode
        ? 'bg-gradient-to-br from-yellow-600 to-orange-600'
        : 'bg-gradient-to-br from-yellow-500 to-orange-500';
    }
    return isDarkMode
      ? 'bg-gradient-to-br from-red-600 to-orange-600'
      : 'bg-gradient-to-br from-red-500 to-orange-500';
  };

  // Desktop variant - larger circular badge
  if (variant === 'desktop') {
    return (
      <button
        onClick={onClick}
        className={`
          absolute -top-1 -right-1 
          w-6 h-6 
          rounded-full 
          ${getColorClasses()}
          text-white 
          text-xs 
          font-bold
          flex items-center justify-center
          shadow-lg
          ring-2 ring-white dark:ring-gray-900
          hover:scale-110
          transition-all duration-200
          cursor-pointer
          z-10
          ${celebrating ? 'animate-complete-celebration' : 'animate-pulse-subtle'}
          ${className}
        `}
        aria-label={`Profile completion: ${completionPercentage} percent. Click to complete your profile.`}
        role="status"
        title={`Complete your profile (${completionPercentage}%)`}
      >
        {celebrating ? '✓' : completionPercentage}
      </button>
    );
  }

  // Mobile variant - smaller badge
  return (
    <button
      onClick={onClick}
      className={`
        absolute -top-1 -right-1
        w-5 h-5
        rounded-full
        ${getColorClasses()}
        text-white
        text-[10px]
        font-bold
        flex items-center justify-center
        shadow-md
        ring-2 ring-white dark:ring-gray-900
        active:scale-95
        transition-all duration-200
        cursor-pointer
        z-10
        ${celebrating ? 'animate-complete-celebration' : 'animate-pulse-subtle'}
        ${className}
      `}
      aria-label={`Profile completion: ${completionPercentage} percent. Click to complete your profile.`}
      role="status"
    >
      {celebrating ? '✓' : completionPercentage}
    </button>
  );
};

export default ProfileCompletionBadge;
